import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useAuthStore } from "@/lib/api-functions/api-auth";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useToast } from "@/hooks/use-toast";
import {
  UIElement,
  fetchUIElements,
  createUIElement,
  updateUIElement,
  deleteUIElement,
  uiElementsStore,
  getCachedUIElements,
} from "@/lib/stores/uiElementsStore";

// Create context with UI elements-specific interface
interface UIElementsDataContextType {
  uiElements: UIElement[];
  isLoading: boolean;
  error: string | null;
  loadData: () => Promise<void>;
  createUIElement: (item: {
    game_id: string;
    name: string;
    html: string;
    css?: string;
    tags?: string[];
    status?: 'draft' | 'published';
  }) => Promise<UIElement>;
  updateUIElement: (item: {
    id: string;
    game_id: string;
    name: string;
    html: string;
    css?: string;
    tags?: string[];
    status?: 'draft' | 'published';
  }) => Promise<UIElement>;
  deleteUIElement: (id: string, gameId: string) => Promise<void>;
}

const UIElementsDataContext = createContext<UIElementsDataContextType | null>(null);

// Separate component that only renders when authenticated and has a selected game
const AuthenticatedUIElementsDataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuthStore();
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();
  const [isInitialized, setIsInitialized] = useState(false);
  const [uiElements, setUIElements] = useState<UIElement[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!selectedGame?.id) return;

    setIsLoading(true);
    setError(null);
    try {
      // fetchUIElements now returns cached data immediately if available
      const fetchedUIElements = await fetchUIElements(selectedGame.id, toast);
      setUIElements(fetchedUIElements);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load UI elements";
      setError(message);
      console.error("UIElementsDataProvider - Error loading UI elements:", err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedGame?.id, toast]);

  const createUIElementHandler = async (item: {
    game_id: string;
    name: string;
    html: string;
    css?: string;
    tags?: string[];
    status?: 'draft' | 'published';
  }) => {
    const newUIElement = await createUIElement(item, toast);
    return newUIElement;
  };

  const updateUIElementHandler = async (item: {
    id: string;
    game_id: string;
    name: string;
    html: string;
    css?: string;
    tags?: string[];
    status?: 'draft' | 'published';
  }) => {
    const updatedUIElement = await updateUIElement(item, toast);
    // The store is already updated with optimistic update, and our subscription will handle the state update
    return updatedUIElement;
  };

  const deleteUIElementHandler = async (id: string, gameId: string) => {
    await deleteUIElement(id, gameId, toast);
    // The store is already updated with optimistic update, and our subscription will handle the state update
    toast({
      title: "Success",
      description: "UI element deleted successfully",
    });
  };

  // Load cached data immediately when game changes, then fetch fresh data
  useEffect(() => {
    if (!selectedGame?.id) return;

    // Load cached data immediately for instant response
    const cachedUIElements = getCachedUIElements(selectedGame.id);
    if (cachedUIElements.length > 0) {
      setUIElements(cachedUIElements);
    }
    
    // Always fetch fresh data (which will be fast if cached, or do background sync)
    if (!isInitialized) {
      loadData();
      setIsInitialized(true);
    }
  }, [selectedGame?.id, isInitialized, loadData]);

  // Reset initialization when selected game changes
  useEffect(() => {
    setIsInitialized(false);
  }, [selectedGame?.id]);

  // Subscribe to store changes for real-time updates
  useEffect(() => {
    if (!selectedGame?.id) return;

    const unsubscribe = uiElementsStore.subscribe(() => {
      // Get the latest data from the store and filter by current game
      const allUIElements = uiElementsStore.getData();
      const gameUIElements = allUIElements.filter(
        (el) => el.game_id === selectedGame.id
      );
      setUIElements(gameUIElements);
    });

    return () => unsubscribe();
  }, [selectedGame?.id]);

  const value: UIElementsDataContextType = {
    uiElements,
    isLoading,
    error,
    loadData,
    createUIElement: createUIElementHandler,
    updateUIElement: updateUIElementHandler,
    deleteUIElement: deleteUIElementHandler,
  };

  return (
    <UIElementsDataContext.Provider value={value}>
      {children}
    </UIElementsDataContext.Provider>
  );
};

export const UIElementsDataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <UIElementsDataContext.Provider value={null}>
        {children}
      </UIElementsDataContext.Provider>
    );
  }

  // If authenticated, render the authenticated provider
  if (isAuthenticated && user?.id) {
    return (
      <AuthenticatedUIElementsDataProvider>
        {children}
      </AuthenticatedUIElementsDataProvider>
    );
  }

  // If not authenticated, provide null context
  return (
    <UIElementsDataContext.Provider value={null}>
      {children}
    </UIElementsDataContext.Provider>
  );
};

export const useUIElementsDataContext = () => {
  const context = useContext(UIElementsDataContext);
  if (!context) {
    throw new Error(
      "useUIElementsDataContext must be used within UIElementsDataProvider and user must be authenticated"
    );
  }
  return context;
};

